// src/components/Auth/VerifyEmailPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext'; // Correctly import useAuth
// --- Firebase imports needed for direct SDK calls if any ---
import { getAuth } from 'firebase/auth'; // <--- IMPORT getAuth
import { app } from '../../firebase';
import Spinner from '../Spinner';
import './VerifyEmailPage.css';

const VerifyEmailPage = () => {
  // Destructure both currentUser AND your wrapped sendEmailVerification function
  const { currentUser, saveUserProfile, sendEmailVerification, logout } =
    useAuth();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isPageLoading, setIsPageLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const userData = location.state?.userData; // From SignupPage

  useEffect(() => {
    if (!currentUser) {
      console.warn('[VerifyEmailPage] No currentUser, redirecting to login.');
      navigate('/login', { replace: true });
    }
  }, [currentUser, navigate]);

  const handleSendVerification = async () => {
    if (!currentUser) {
      setError('Not logged in. Cannot send verification email.');
      return;
    }
    setMessage('');
    setError('');
    setIsPageLoading(true);
    try {
      // 1. Determine where we are (Localhost or Production)
      // window.location.origin gives us 'http://localhost:3000' or 'https://influencersforce.com'
      const currentUrl = window.location.origin;

      // 2. Define the settings
      // We point the URL to the /forgot-password route because your AppContent.js
      // handles the 'mode=verifyEmail' logic inside the /forgot-password component
      // (or whichever route handles the 'oobCode').

      // NOTE: Based on your router, the logic for handling the code
      // seems to live in ResetPasswordPage (mapped to /forgot-password).
      // So we must direct the link there.
      const actionCodeSettings = {
        url: `${currentUrl}/forgot-password`,
        handleCodeInApp: true,
      };
      console.log(
        '[VerifyEmailPage] Sending verification email with redirect to:',
        actionCodeSettings.url,
      );
      console.log(
        "[VerifyEmailPage] Calling context's sendEmailVerification with currentUser:",
        currentUser,
      );
      // Call the WRAPPED function from AuthContext, passing the currentUser object to it
      await sendEmailVerification(currentUser); // <-- CORRECTED CALL

      setMessage(
        'Verification email sent! Please check your inbox (and spam folder).',
      );
    } catch (err) {
      // Now this catch block will receive errors from your AuthContext wrapper,
      // which in turn come from firebaseSendEmailVerification(currentUser)
      console.error(
        'Error from AuthContext sendEmailVerification in VerifyEmailPage:',
        err,
      );
      let displayError =
        'Failed to send verification email. Please try again in a few moments.';
      if (err && err.code === 'auth/too-many-requests') {
        displayError =
          'Too many requests. Please wait a while before trying to resend the verification email.';
      } else if (err && error.code === 'auth/network-request-failed') {
        displayError =
          'Your network connection was lost. Please check your internet connection and try again.';
      } else if (err && err.message) {
        displayError = err.message;
      }
      setError(displayError);
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    if (!currentUser) {
      setError('Not logged in. Cannot check verification status.');
      return;
    }
    console.log('[VerifyEmailPage] Initiating email verification check...');
    setMessage(''); // Clear previous success messages
    setError(''); // Clear previous error messages
    setIsPageLoading(true);

    try {
      // 1. Reload the currentUser object from context.
      // This updates the properties of the 'currentUser' instance in this component's scope.
      await currentUser.reload();
      console.log('[VerifyEmailPage] currentUser.reload() completed.');

      // 2. For the most definitive status immediately after reload,
      // get a fresh reference to the currentUser from the Auth service.
      const authService = getAuth(app); // Get the Firebase Auth service instance
      const freshUser = authService.currentUser; // Get the current user from this service instance

      if (freshUser && freshUser.emailVerified) {
        console.log(
          '[VerifyEmailPage] Email verified! (Checked via freshUser object after reload)',
        );

        // 3. If this check is part of a signup flow (userData is present from SignupPage),
        // attempt to save/finalize the user profile.
        if (userData) {
          console.log(
            '[VerifyEmailPage] UserData (from signup) present. Attempting to save profile...',
          );
          try {
            // Assuming saveUserProfile is from useAuth() and handles token, etc.
            // It expects 'userData' which is typically { userName, email } from the signup form.
            await saveUserProfile(userData);
            console.log(
              '[VerifyEmailPage] User profile saved/updated successfully after verification.',
            );
          } catch (profileError) {
            console.error(
              '[VerifyEmailPage] Failed to save profile after verification:',
              profileError,
            );
            setError(
              'Email verified, but failed to finalize your profile setup. Please try logging in, or contact support if the issue persists.',
            );
            setIsPageLoading(false); // Stop loading
            return; // Important: Do not navigate away if profile save fails critically
          }
        } else {
          console.log(
            '[VerifyEmailPage] No UserData from signup flow. Profile assumed to be already created or managed elsewhere.',
          );
        }

        setIsPageLoading(false);
        console.log(
          '[VerifyEmailPage] Verification check successful. Navigating to home page (/).',
        );
        navigate('/', { replace: true }); // Navigate to home or dashboard
      } else {
        setIsPageLoading(false);
        console.warn(
          '[VerifyEmailPage] Email not verified yet. (Checked via freshUser object after reload)',
        );
        setError(
          'Your email is not verified yet. Please check your email inbox (and spam folder) for the verification link, or try resending it.',
        );
      }
    } catch (err) {
      setIsPageLoading(false);
      console.error(
        '[VerifyEmailPage] Error during verification check or profile save operation:',
        err,
      );
      // Provide a generic error, or inspect 'err.code' for specific Firebase errors
      setError(
        'An error occurred while checking your verification status. Please try again.',
      );
    }
  };
  // --- END OF CORRECTED handleCheckVerification ---

  const handleLogout = async () => {
    setIsPageLoading(true);
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      setError('Logout failed. Please try again.');
    } finally {
      setIsPageLoading(false);
    }
  };

  if (!currentUser) {
    return <Spinner message="Loading user information..." />;
  }

  return (
    <div className="verify-email-page">
      {isPageLoading && <Spinner message="Processing..." />}
      <h2>Verify Your Email</h2>
      <p>
        Please click on the "Send/Resend" button below for a verification email
        to be sent to <strong>{currentUser.email}</strong>.
        <br />
        Then please check your inbox (and spam folder) and click the link to
        verify your account.
      </p>
      <div className="action-buttons">
        <button onClick={handleSendVerification} disabled={isPageLoading}>
          Send/Resend Verification Email
        </button>
        <button onClick={handleCheckVerification} disabled={isPageLoading}>
          I've Verified My Email
        </button>
        <button
          onClick={handleLogout}
          disabled={isPageLoading}
          className="logout-button"
        >
          {' '}
          {/* Added className */}
          Logout & Verify Later
        </button>
      </div>
      {message && <p className="success-message">{message}</p>}
      {error && <p className="error-message">{error}</p>}
    </div>
  );
};

export default VerifyEmailPage;
